import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridSortModel, GridFilterModel } from '@mui/x-data-grid';
import CustomPagination from 'components/common/pagination/pagination';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/pagination';
import { commonGridStyles } from 'styles/gridStyles';
import commonStyles from 'styles/common.module.scss';
import { siteWheelService } from 'components/api/siteWheel';
import { formatDate } from 'utils/date';

interface Props {
  siteId: string | number;
}

const WheelUsersList: React.FC<Props> = ({ siteId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortValue, setSortValue] = useState<'ASC' | 'DESC' | undefined>();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await siteWheelService.getSpins(siteId, {
        page,
        limit,
        sort: sortField ? `${sortField},${sortValue || 'DESC'}` : undefined,
      });
      const resData = response.data || response;
      setData(resData.docs || []);
      setTotal(resData.total || 0);
    } catch (error) {
      console.error('Failed to fetch spins:', error);
    } finally {
      setIsLoading(false);
    }
  }, [siteId, page, limit, sortField, sortValue]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, filterable: false },
    {
      field: 'userId',
      headerName: 'User',
      width: 90,
      filterable: false,
      renderCell: (params) => (
        <span
          // className={styles.userLink}
          style={{ color: '#0f97ff' }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/clients/${params.row.userId}`);
          }}
        >
          {params.row.username || '-'}
        </span>
      ),
    },
    {
      field: 'prizeType',
      headerName: 'Prize',
      width: 260,
      filterable: false,
      renderCell: (params) => {
        const type = (params.value as string) || '';
        const row = params.row || {};

        if (type === 'free_spin') {
          const freeSpinGame = row.freeSpinGame;
          const gameTitle = freeSpinGame?.title || 'Free Spin';
          const countText = row.freeSpinCount != null ? ` - ${row.freeSpinCount} sp.` : '';
          const fullTitle = `${gameTitle}${countText}`;

          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                width: '100%',
                overflow: 'hidden',
              }}
            >
              {freeSpinGame?.imageUrl && (
                <Box
                  component="img"
                  src={freeSpinGame.imageUrl}
                  alt={gameTitle}
                  sx={{
                    width: 36,
                    height: 26,
                    borderRadius: '4px',
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography
                noWrap
                variant="body2"
                title={fullTitle}
                sx={{
                  fontSize: '13px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flexShrink: 1,
                }}
              >
                {gameTitle}
              </Typography>
              {row.freeSpinCount != null && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {countText}
                </Typography>
              )}
            </Box>
          );
        }

        if (type === 'cash') {
          return (
            <Typography variant="body2" sx={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
              {row.prizeAmount != null ? `Cash - $${row.prizeAmount}` : 'Cash'}
            </Typography>
          );
        }

        if (type === 'deposit_match') {
          return (
            <Typography variant="body2" sx={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
              {row.depositMatchPercent != null
                ? `Deposit Match Bonus - ${row.depositMatchPercent}%`
                : 'Bonus'}
            </Typography>
          );
        }

        return type ? type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '-';
      },
    },
    {
      field: 'probability',
      headerName: 'Probability',
      width: 120,
      filterable: false,
      renderCell: (params) => (params.value ? `${params.value}%` : '-'),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      filterable: false,
      renderCell: (params) => {
        const status = (params.value as string) || '';
        const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : '-';

        const colorMap: Record<string, { bg: string; color: string }> = {
          claimed: { bg: 'rgba(76, 175, 80, 0.15)', color: '#4caf50' },
          pending: { bg: 'rgba(255, 167, 38, 0.15)', color: '#ffa726' },
          expired: { bg: 'rgba(244, 67, 54, 0.15)', color: '#f44336' },
          rejected: { bg: 'rgba(244, 67, 54, 0.15)', color: '#f44336' },
          cancelled: { bg: 'rgba(158, 158, 158, 0.15)', color: '#9e9e9e' },
        };

        const style = colorMap[status.toLowerCase()] || {
          bg: 'rgba(158, 158, 158, 0.15)',
          color: '#9e9e9e',
        };

        return (
          <Box
            sx={{
              backgroundColor: style.bg,
              color: style.color,
              fontWeight: 600,
              fontSize: '12px',
              padding: '3px 10px',
              borderRadius: '4px',
              display: 'inline-block',
            }}
          >
            {label}
          </Box>
        );
      },
    },
    {
      field: 'spunAt',
      headerName: 'Spun At',
      width: 170,
      filterable: false,
      renderCell: (params) => (params.value ? formatDate(params.value) : '-'),
    },
    {
      field: 'claimedAt',
      headerName: 'Claimed At',
      width: 170,
      filterable: false,
      renderCell: (params) => (params.value ? formatDate(params.value) : '-'),
    },
  ];

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      setSortField(model[0].field);
      setSortValue(model[0].sort?.toUpperCase() as 'ASC' | 'DESC');
    } else {
      setSortField(undefined);
      setSortValue(undefined);
    }
  };

  const handleFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model);
  };

  const renderPagination = () => (
    <CustomPagination
      count={total || 0}
      page={page - 1}
      onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
      rowsPerPage={limit}
      onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    />
  );

  return (
    <Box className={commonStyles.gridWrapper}>
      {renderPagination()}
      <DataGrid
        rows={data || []}
        rowCount={total || 0}
        getRowId={(row: any) => row.id}
        columns={columns}
        onSortModelChange={handleSortModelChange}
        loading={isLoading}
        sx={commonGridStyles}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
      />
      {renderPagination()}
    </Box>
  );
};

export default WheelUsersList;
